import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { EyeSlashIcon } from '@heroicons/react/24/outline';
import moment from 'moment';

import Header from '../../../Header';
import GridContainer from '../../../Grid/Container';
import GridItem from '../../../Grid/Item';
import Badge from '../../../Badge';
import { PrimaryCTA, SecondaryCTA } from '../../../Buttons';

const lifecycleBadgeType = {
  'submitted': 'warning',
  'approved': 'success',
  'rejected': 'error',
  'published': 'success',
  'archived': 'warning',
};

const visibilityBadgeType = {
  'public': 'success',
  'incognito': 'warning',
  'offline': 'error',
};

export default function ListingStatsHeader() {
  const listing = useSelector((state) => state.listing);
  const env = useSelector((state) => state.env);

  const [searchURL, setSearchURL] = useState(null);
  const searchRadius = 0.005;

  useEffect(() => {
    if (listing) {
      const langPrefix = env.userLanguage === 'en' ? '' : `/${env.userLanguage}`;
      setSearchURL(`${langPrefix}/search?`
        + `address=${listing.address}&`
        + `lat_g=${listing.display_latitude - searchRadius}&`
        + `lat_l=${listing.display_latitude + searchRadius}&`
        + `lng_g=${listing.display_longitude - searchRadius}&`
        + `lng_l=${listing.display_longitude + searchRadius}&`
        + `latitude=${listing.display_latitude}&`
        + `longitude=${listing.display_longitude}&`
        + `zoom=16`);
    }
  }, [listing, env.userLanguage]);

  const manageHref = env.userLanguage === 'en'
    ? `/listings/${listing.id}/manage`
    : `/${env.userLanguage}/listings/${listing.id}/manage`;

  return (
    <Header colorScheme="neutral" className="p-4 !border-3 border-dashed border-gray-200 bg-slate-100 md:rounded-lg md:my-2">
      <div className="mb-4 flex items-center text-gray-700">
        <EyeSlashIcon width={16} strokeWidth={2} />
        <div className="ml-1 font-medium grow">Admin-only section</div>
        <PrimaryCTA className="!mt-0" type="success" href={manageHref} target="_self">
          Manage listing
        </PrimaryCTA>
      </div>

      <GridContainer colsMin={2} colsMax={4}>
        <GridItem label="Lifecycle">
          <Badge label={listing.lifecycle_status} type={lifecycleBadgeType[listing.lifecycle_status]} />
        </GridItem>

        <GridItem label="Visibility">
          <Badge label={listing.visibility_status} type={visibilityBadgeType[listing.visibility_status]} />
        </GridItem>

        <GridItem label="Boosted">
          <Badge label={listing.is_boosted ? 'Yes' : 'No'} type={listing.is_boosted ? 'success' : 'warning'} />
        </GridItem>

        <GridItem label="Top space">
          <Badge label="-" />
        </GridItem>
      </GridContainer>

      <GridContainer colsMin={3} colsMax={6}>
        <GridItem label="Views">{listing.view_count}</GridItem>
        <GridItem label="Requests">{listing.inquiry_count}</GridItem>
        <GridItem label="Transactions">{listing.transaction_count}</GridItem>
        <GridItem label="Last updated" colSpan={3}>{moment.utc(listing.updated_at).format('LLL')}</GridItem>
      </GridContainer>

      <GridContainer colsMin={1} colsMax={2}>
        <GridItem label="Address">
          <div>{listing.address}</div>
          <div className="mt-3 flex">
            <SecondaryCTA href={`https://www.google.com/maps/place/${listing.address}`} target="_blank">GMaps Search</SecondaryCTA>
            { searchURL && <SecondaryCTA href={searchURL} target="_blank">Platform Search</SecondaryCTA> }
          </div>
        </GridItem>
        <GridItem label="Salesforce">
          <div className="flex grow items-center">
            {
              listing.external_mappings.ref_number && (
                <a className="mr-2 underline text-gray-900"
                   href={`${env.salesforce.baseUrl}${listing.external_mappings.listing_salesforce_id}`}
                   target="_blank">
                  #{listing.external_mappings.ref_number}
                </a>
              )
            }
            <Badge withDot label={listing.external_mappings.ref_number ? "Synced" : "Not synced"} type={listing.external_mappings.ref_number ? "success" : "error"}/>
          </div>
          <div className="mt-3">
            <SecondaryCTA type='neutral'>Re-sync</SecondaryCTA>
          </div>
        </GridItem>
      </GridContainer>
    </Header>
  );
}
